import React, { ReactNode, useEffect, useState } from 'react';

import type { EnquiryDTO, CompanyInfoDTO, MemberIdTypeDTO } from '../../services/apiClient';

import AuthLayout from '../../components/layout/AuthLayout';
import { useEnquiryApi } from '../../hooks/useEnquiryApi';
import { showToast } from '../../utils/showToast';
import FormField from '../../components/common/FormField';
import Button from '../../components/common/Button';
import { useEnquiryTypeApi } from '../../hooks/useEnquiryTypeApi';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import handleChange from '../../utils/formUtils/handleChange';
import { useCompanyInfoApi } from '../../hooks/useCompanyInfoApi';
import { CompanyConstants } from '../../constants/CompanyConstants';
import { useMemberIdTypes } from '../../hooks/useMemberIdTypeApi';
import { MemberIdTypeConstants } from '../../constants/MemberIdType';

const Contact: React.FC = () => {
    const { create, isLoading } = useEnquiryApi();
    const [formData, setFormData] = useState<EnquiryDTO>({} as EnquiryDTO);
    const [memberIdTypeId, setMemberIdTypeId] = useState<string>(MemberIdTypeConstants.SOUTH_AFRICAN_CITIZEN);

    // Define the type for dropdown options
    type DropdownOption = { value: string; label: ReactNode };
    const [enquiryTypeOptions, setEnquiryTypeOptions] = useState<DropdownOption[]>([]);
    const { search: searchEnquiryTypes, isLoading: isLoadingEnquiryTypes } = useEnquiryTypeApi();
    const { data: idTypesData, isLoading: isLoadingIdTypes } = useMemberIdTypes();

    const { getById: getCompanyInfoById } = useCompanyInfoApi();
    const [companyInfo, setCompanyInfo] = useState<CompanyInfoDTO | null>(null);

    useEffect(() => {
        const loadDropdownData = async () => {
            try {
                const enquiryTypes = await searchEnquiryTypes({
                    filters: {
                        isActive: true
                    },
                    page: 1,
                    pageSize: 999,
                    sortBy: 'DisplayPosition'
                });

                setEnquiryTypeOptions(
                    enquiryTypes?.data?.results?.map((et) => ({
                        value: et.id ?? '',
                        label: et.title ?? '',
                    })) || []
                );
            } catch (error) {
                console.error('Error loading enquiry types:', error);
            }
        };

        const loadCompanyInfo = async () => {
            try {
                const response = await getCompanyInfoById(CompanyConstants.NATSHOOT);
                if (response) {
                    setCompanyInfo(response.data || null);
                }
            } catch (error) {
                console.error('Error fetching company info:', error);
            }
        };

        loadDropdownData();
        loadCompanyInfo();
    }, []);

    const validateEmail = (email: string): boolean => {
        if (!email) return true; // if empty, validation passes (optional field)
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(email.trim());
    };

    const validateCellphone = (cellNumber: string): boolean => {
        if (!cellNumber) return true; // if empty, validation passes (optional field)
        const cleaned = cellNumber.replace(/\s+/g, ''); // remove spaces
        const pattern = /^(0(6|7|8)[0-9]{8}|27(6|7|8)[0-9]{8})$/;
        return pattern.test(cleaned);
    };

    const validationRules: ValidationRules = {
        enquiryTypeId: [{ required: true, message: 'Enquiry Type is required' }],
        name: [{ required: true, message: 'Name is required' }],
        surname: [{ required: true, message: 'Surname is required' }],
        emailAddress: [
            { required: true, message: 'Email is required' },
            { custom: (value) => validateEmail(String(value || '')), message: 'Email is not valid' }
        ],
        contact: [
            { required: true, message: 'Cellphone is required' },
            { custom: (value) => validateCellphone(String(value || '')), message: 'Cellphone is not valid' }
        ],
        contactRequest: [{ required: true, message: 'Enquiry is required' }],
        idPassportNumber: [
            { required: true, message: memberIdTypeId === MemberIdTypeConstants.SOUTH_AFRICAN_CITIZEN ? 'ID Number is required' : 'Passport Number is required' }
        ]
    };

    const { errors, validate } = useFormValidation<EnquiryDTO>(validationRules);

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (validate(formData)) {
            try {
                await create(formData);
                showToast('Successfully submitted application. We will get back to you regarding your enquiry.', 'success');
                // Reset form
                setFormData({} as EnquiryDTO);
            } catch (error) {
                console.error('Error submitting enquiry:', error);
                showToast('Failed to submit enquiry. Please try again.', 'error');
            }
        }
    };

    return (
        <AuthLayout
            title="Contact Us"
            description="Please provide your enquiry details and we will get back to you"
        >
            {/* Office Details Section */}
            <div className="mb-8 text-center space-y-2">
                <p className="text-gray-600">
                    Office open week day's <span className="font-semibold">08:00 – 16:00</span>.
                </p>
                {companyInfo?.phone && (
                    <p className="text-gray-600 font-semibold">{companyInfo.phone}</p>
                )}
                {companyInfo?.address && (
                    <p className="text-gray-600">{companyInfo.address}</p>
                )}
                <div className="my-4 border-t border-gray-300 mx-auto w-24"></div>
                {/* Document verification link - currently disabled */}
                <p className="text-gray-600">
                    Verify documents and certificates{' '}
                    <a href="/verify-certificate" className="text-primary hover:underline">
                        here
                    </a>
                </p>
            </div>

            <div className="mt-8">
                <form onSubmit={handleSubmit}>
                    <FormField
                        id="enquiryTypeId"
                        label="Enquiry Type"
                        type="dropdown"
                        value={formData.enquiryTypeId || ''}
                        onChange={(e) => handleChange(e, setFormData)}
                        placeholder="Select Enquiry Type"
                        required={true}
                        error={errors.enquiryTypeId}
                        options={enquiryTypeOptions}
                        isLoading={isLoadingEnquiryTypes}
                    />
                    <FormField
                        id="name"
                        label="Name"
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => handleChange(e, setFormData)}
                        placeholder="e.g John"
                        required={true}
                        error={errors.name}
                        textCase="titlecase"
                    />
                    <FormField
                        id="surname"
                        label="Surname"
                        type="text"
                        value={formData.surname || ''}
                        onChange={(e) => handleChange(e, setFormData)}
                        placeholder="e.g Doe"
                        required={true}
                        error={errors.surname}
                        textCase="titlecase"
                    />
                    <FormField
                        id="memberIdTypeId"
                        label="Citizenship?"
                        type="dropdown"
                        value={memberIdTypeId || ''}
                        onChange={(e) => setMemberIdTypeId(e.target.value)}
                        placeholder="Select Citizenship"
                        required={true}
                        error={errors.memberIdTypeId}
                        options={(idTypesData || []).map((it) => ({
                            value: it.id ?? '',
                            label: it.title ?? '',
                        }))}
                        isLoading={isLoadingIdTypes}
                    />
                    <FormField
                        id="idPassportNumber"
                        label={memberIdTypeId === MemberIdTypeConstants.OTHER ? "Passport Number" : "ID Number"}
                        type="text"
                        value={formData.idPassportNumber || ''}
                        onChange={(e) => handleChange(e, setFormData)}
                        placeholder={memberIdTypeId === MemberIdTypeConstants.OTHER ? 'e.g AB56796-92' : 'e.g 8101015025086'}
                        required={true}
                        error={errors.idPassportNumber}
                    />
                    <FormField
                        id="emailAddress"
                        label="Email"
                        type="email"
                        value={formData.emailAddress || ''}
                        onChange={(e) => handleChange(e, setFormData)}
                        placeholder="e.g john@doe.com"
                        textCase='lowercase'
                        required={true}
                        error={errors.emailAddress}
                    />
                    <FormField
                        id="contact"
                        label="Cellphone"
                        type="text"
                        value={formData.contact || ''}
                        onChange={(e) => handleChange(e, setFormData)}
                        placeholder="e.g 0831234567"
                        required={true}
                        error={errors.contact}
                    />
                    <FormField
                        id="contactRequest"
                        label="Enquiry"
                        type="textarea"
                        value={formData.contactRequest || ''}
                        onChange={(e) => handleChange(e, setFormData)}
                        placeholder="Please provide your enquiry here"
                        required={true}
                        error={errors.contactRequest}
                    />

                    <div className="my-6">
                        <Button
                            type="submit"
                            className="w-full"
                            isLoading={isLoading}
                            disabled={isLoading}
                        >
                            Submit Enquiry
                        </Button>
                    </div>
                </form>
            </div>
        </AuthLayout>
    );
};

export default Contact;
